import { Button, CircularProgress, Dialog, DialogActions } from 'drift-ui';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/configureStore';
import { SET_DOT_CHECK_RESPONSE } from 'app/actionTypes';
import { Company, DO_APPLICATION_TYPE } from 'client-experience-toolbox';
import DotCheckError from '../assets/images/dotCheckError.svg';
import PrefillFound from '../assets/images/prefill_found.svg';
import css from './DotCheckModal.scss';
import cx from 'classnames';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';

export type PrefillState = 'preview' | 'loading' | 'error';

interface PrefillFMCSAModalProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
}

const LoadingModal: FC = () => {
  const { t } = useTranslation();
  return (
    <div className={css.dialogContainer}>
      <div className={css.container}>
        <p className={css.headerTitle}>{t('prefill_modal.loading')}</p>
        <div className={css.progressContainer}>
          <CircularProgress size={56} />
        </div>
      </div>
    </div>
  );
};

const ErrorModal: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  return (
    <div className={cx(css.container, css.errorContainer)}>
      <p className={css.headerTitle}>{t('prefill_modal.error_title')}</p>
      <p>{t('prefill_modal.error_description')}</p>
      <div className={css.errorImageContainer}>
        <img
          src={DotCheckError}
          alt={t('dot_check.alt_error_image')}
          width={150}
          height={150}
          className={css.dotCheckErrorImage}
        />
      </div>
      <div className={css.actionsContainer}>
        <DialogActions className={css.dialogActions}>
          <Button variant="contained" onClick={onClose}>
            {t('Close')}
          </Button>
        </DialogActions>
      </div>
    </div>
  );
};

const PreviewModal: FC<{
  prefillData?: Record<string, Partial<Company>>;
  onApply: () => void;
  onSkip: () => void;
}> = ({ prefillData, onApply, onSkip }) => {
  const { t } = useTranslation();

  if (!prefillData) {
    return (
      <div className={css.dialogContainer}>
        <div className={css.container}>
          <p className={css.headerTitle}>{t('prefill_modal.loading')}</p>
          <div className={css.progressContainer}>
            <CircularProgress size={56} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cx(css.container, css.foundContainer)}>
      <p className={css.headerTitle}>{t('prefill_modal.found_title')}</p>
      <div className={css.foundImageContainer}>
        <img
          src={PrefillFound}
          alt={t('dot_check.alt_found_image')}
          width={200}
          height={164}
          className={css.dotCheckFoundImage}
        />
      </div>
      <p>{t('prefill_modal.found_description')}</p>
      <div className={css.actionsContainer}>
        <DialogActions className={css.dialogActions}>
          <Button variant="outlined" onClick={onSkip}>
            {t('skip')}
          </Button>
          <Button variant="contained" onClick={onApply}>
            {t('prefill_modal.prefill_button')}
          </Button>
        </DialogActions>
      </div>
    </div>
  );
};

export const PrefillFMCSAModal: FC<PrefillFMCSAModalProps> = ({ setShowModal, showModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentModal, setCurrentModal] = useState<PrefillState>('preview');
  const dotCheckResponse = useSelector((state: RootState) => state.applicationReducer.dotCheckResponse);
  const { applyPrefillData } = useFormNetworkCalls(DO_APPLICATION_TYPE.FACTORING);

  const handleApplyPrefill = async () => {
    setCurrentModal('loading');
    try {
      await applyPrefillData(dotCheckResponse!.prefillData!);
      dispatch({ type: SET_DOT_CHECK_RESPONSE, dotCheckResponse: undefined });
      setShowModal(false);
      setCurrentModal('preview');
    } catch (error) {
      console.error('Failed to apply prefill data:', error);
      dispatch({ type: SET_DOT_CHECK_RESPONSE, dotCheckResponse: undefined });
      setCurrentModal('error');
    }
  };

  const handleSkip = () => {
    dispatch({ type: SET_DOT_CHECK_RESPONSE, dotCheckResponse: undefined });
    setShowModal(false);
    setCurrentModal('preview');
  };

  const handleErrorClose = () => {
    setShowModal(false);
    setCurrentModal('preview');
  };

  const renderModalContent = () => {
    switch (currentModal) {
      case 'loading':
        return <LoadingModal />;
      case 'preview':
        return (
          <PreviewModal prefillData={dotCheckResponse?.prefillData} onApply={handleApplyPrefill} onSkip={handleSkip} />
        );
      case 'error':
        return <ErrorModal onClose={handleErrorClose} />;
    }
  };

  return (
    <Dialog open={showModal} aria-label={t('dot_check.dialog_aria_label')}>
      {renderModalContent()}
    </Dialog>
  );
};
